import { Config } from 'config';

import { find } from 'lodash';

import { resolve, Magnitude, imagePromise,
         setLineDash, strokeWireframe } from 'gear';

import { Xml } from '../util';
import { Point, Matrix, Bounds, Frame, Curve, Segment } from '../geometry';
import { MonochromeColors, EmptyPen } from '../style';
import { hslShift_gl } from '../style/hsl-shift';

import { Figure } from './figure';
import { Region, Polyregion, GShape } from './shape';
import { QuantumImage } from './detail/quantum-image';
import { PowerClipContent } from './power-clip';

const _invalidate = function() { this.invalidate(); };

export const Raster = Figure.Raster = Figure.extend({ typeName: 'Raster',

  type: 'Raster',

  Properties: {
    imageLink         : { type: 'string'   , def: null, dropDef:true, onChange: _invalidate },
    encodedImage      : { type: 'string'   , def: null, dropDef:true, onChange: _invalidate },
    width             : { type: 'magnitude', def: new Magnitude(0,'px'), dropDef:true },
    height            : { type: 'magnitude', def: new Magnitude(0,'px'), dropDef:true },
    dpi               : { type: 'number'   , def: 25.4, tag: 'DPI', onChange: _invalidate },
    hueShift          : { type: 'number'   , def: 0.0, onChange: _invalidate},
    saturationShift   : { type: 'number'   , def: 0.0, onChange: _invalidate},
    lightShift        : { type: 'number'   , def: 0.0, onChange: _invalidate},
    colorMap          : { type: 'ColorMap' , def: null, dropDef:true, onChange: _invalidate }
  },

  get isRaster   () { return true; },
  get isMonochrome() { return this._colorMap instanceof MonochromeColors; },

  init(domNode) {
    this._cachedImage = null;
    this.callParent(domNode);
  },

  containsLinkedResources() {
    return true;
  },

  canBeProcessedLocally() {
    return ! this.needsCacheToRender();
  },

  _representationType() {
    return this.needsCacheToRender() ? 'server' : 'local';
  },

  needsCacheToRender() {
    if( ! Config.renderLocallyWhenPossible ) {
      return true ;
    }
    return ! Config.localRaster;
  },

  quickToDraw() {
    return false; // ! this._colorMap;
  },

  preparedToDraw( zoom ) {
    if (this._encodedImage != null)
    {
      return true;
    }
    else
    {
      const quantumSize = Config.bypassQuantums ? null : this.quantumSizeToDraw(zoom);
      return !! QuantumImage.get(this._imageLink, quantumSize);
    }
  },

  prepareToDraw$( zoom ) {
    if (this._encodedImage != null)
    {
        // do something ?
    }
    else
    {
      const quantumSize = Config.bypassQuantums ? null : this.quantumSizeToDraw(zoom);
      return QuantumImage.cache$(this._imageLink, quantumSize);
    }
  },

  quantumSizeToDraw( zoom ) {
    const frame = this.globalFrame_();

    const vw = frame.width  * zoom;
    const vh = frame.height * zoom;

    const wpx = this.width_px(),
        hpx = this.height_px();

    return QuantumImage.sizeToDraw(vw,vh, wpx,hpx);
  },

  _draw( context, transform, config, overlayedNoClip = false ) {
    const _this = this;

    context.save();

    if (!overlayedNoClip) {
      const [lClipContent, lClipParent] = this.getClippedBy();
      if (lClipContent) {
        if (lClipContent.editingClip) {
          context.restore();
          _this._draw(context, transform, config, true);
          context.save();
        }
        let lOldTransform = transform;
        if (!lClipContent.clipAbsolute && !lClipContent.editingClip) {
          transform = Matrix.m(transform, lClipParent.globalMatrix_());
        }
        lClipContent.clipContext(context, transform);
        //DEBUG
        // const ps = lClipContent._polyregions;
        // for(let k=0,kEnd=ps.length;k<kEnd;++k) { const p = ps[k];
        //   p.draw(context,transform,config);
        // }
        //END DEBUG
        transform = lOldTransform;    
      }
    }    

    const wireframe = config && config.wireframe;
    if( wireframe ) {
      context.globalAlpha *= 0.5;
    }

    const imageLink = _this._imageLink,
        width_px = _this.width_px(), height_px = _this.height_px();

    // Draw the raster using the best resolution we have til the moment
    const zoom = transform.scaleFactor();

    let quantumSize = _this.quantumSizeToDraw(zoom);

    const colorMap = this._colorMap;
    const colorMapImageMaxSize = Config.colorMapImageMaxSize;

    if( colorMap && (quantumSize === null || quantumSize > colorMapImageMaxSize) ) {
      // quantumSize could be null if we are retrieving the full image
      // and the full image is larger than the maximum element 
      // in the quantumSizes array in quantum-image.js
      quantumSize = colorMapImageMaxSize;
    }
    else if(Config.bypassQuantums)
    {
      // no color map & bypass => retrieve full image
      quantumSize = null;
    }

    let image;
    if (this._cachedImage != null)
    {
      image = this._cachedImage;
    }
    else if (this._encodedImage != null)
    {  
      setTimeout(() => {
        imagePromise('data:image/png;base64,'+ this._encodedImage).then( (image) => {
            _this._cachedImage = image;
            const topFigure = _this.topFigure;
            if( topFigure ) {
              topFigure.redraw$();
            }
          });
        
      }, 0);

    }
    else
    {
      image = QuantumImage.get(imageLink,quantumSize);

      if( ! image ) {

        // We need to use a timeout here to clean up the call stack
        // This is due to Jquery promises, we need to move forward soon
        setTimeout( () => {
          QuantumImage.cache$(imageLink,quantumSize) .then( () => {
            const topFigure = _this.topFigure;
            if( topFigure ) {
              topFigure.redraw$();
            }
          });
        },0);

        image = QuantumImage.getBest(imageLink,quantumSize);
      }
    }


    let t = Matrix.m( transform, _this.globalMatrix_() );

    t = Matrix.concat( t, Matrix.translate(0,_this.height_mm()), Matrix.scale(1,-1), Matrix.scale( 25.4 / _this._dpi ) );

    if( image ) {

      const imageSize = Math.max(image.width,image.height);

      if( colorMap ) {
        // Pass unique id string for caching, size cache is taken care internally
        // console.log('recolorize');
        let lCM = colorMap;
        if (config.showthroughColor && config.showthroughColor.alpha != 0.0) {
          lCM = colorMap.clone();
          let lNrColors = lCM.colors.length;
          for(let i=0;i<lNrColors;i++) {
            if( lCM.colors[i].showThrough ) {
              lCM.colors[i].rgb = config.showthroughColor.rgb;
              lCM.colors[i].alpha = 1.0;
            }  
          }
        }

        image = lCM.recolorize(image, imageLink);
      }

      const hueShift = this._hueShift;
      const saturationShift = this._saturationShift;
      const lightShift = this._lightShift;
      if (hueShift != 0 || saturationShift != 0 || lightShift != 0) {
        image = hslShift_gl(image, hueShift, saturationShift, lightShift);
      }

      const preImageScale = Math.max(width_px,height_px) / imageSize;

      const matrix = Matrix.m( t, Matrix.scale(preImageScale) );
      matrix.transformContext(context);
      if (Config.smoothRasterOnZoom !== undefined)
        context.imageSmoothingEnabled = Config.smoothRasterOnZoom;
      context.drawImage(image,0,0);
    }
    else {
      // While waiting for the first quantum, draw a semi transparent mask
      t.transformContext(context);
      context.rect(0,0,width_px,height_px);
      context.fillStyle = 'rgba(0, 100, 0, 0.2)';
      context.fill();
    }

    if( wireframe ) {
      const frame = _this.globalFrame_();
      frame.draw(context,transform);

      context.lineWidth = 1;
      setLineDash(context,[4,3]);
      strokeWireframe(context);
    }

    context.restore();

    if (overlayedNoClip) {
      context.save();
      context.beginPath();
      t.transformContext(context);
      context.rect(0,0,width_px,height_px);
      context.fillStyle = 'rgba(255, 255, 255, 0.5)';
      context.fill(this.windingRule);
      context.restore();
    }

  },

  pixelsToMillimeters( pixels) {
    return pixels / this._dpi * 25.4;
  },

  globalDpi$( v ) {
    const me = this;
    return this.update$().then( () => {
      const gdpi = me.globalDpi_();
      if( v === undefined ) {
        return gdpi;
      }
      else{
        return me.scale$( gdpi/v );
      }
    });
  },
  globalDpi_() {
    if (this._owner instanceof PowerClipContent)
    {
      const pc = this._owner;
      if (pc._owner instanceof Figure)
      {
        return this._dpi / (pc._owner.matrix_().scaleFactor()*this.matrix_().scaleFactor());
      }
    }
    var lFinalMatrix = this.matrix_();
    var lParent = this._owner;
    while (lParent != null && lParent.isGroup) {
      lFinalMatrix = Matrix.m(lParent.matrix_(), lFinalMatrix);
      lParent = lParent._owner;
    }

    return this._dpi / lFinalMatrix.scaleFactor();
  },

  boundsIsVectorBounds() { return true; },

  // Only pixels are supported right now
  // We may allow different metrics here, coding in the CDL directly the pixels
  // to avoid any slow down

  width_px() {
    if (this._width) {
      return this._width.toType('px');
    }
    return 0;
  },
  height_px() {
    if (this._height) {
      return this._height.toType('px');
    }
    return 0;
  },

  width_mm() {
    return this.pixelsToMillimeters(this.width_px());
  },
  height_mm() {
    return this.pixelsToMillimeters(this.height_px());
  },

  _primitive_computePreReferencePoint() { return new Point(0,0); },

  _primitive_computePreVectorBounds() {
    if( this._width && this._height && this._dpi ) {
      return new Bounds( 0, 0, this.width_mm(), this.height_mm() );
    }
    else {
      return new Bounds( 0, 0, 100, 100 ) ;
    }
  },
  _primitive_computePreVectorBoundsAfter( transform ) {
    return Frame.fromBounds(this._primitive_computePreVectorBounds()).transformed(transform).bounds;
  },
  _primitive_computePreBounds() {
    return this._primitive_computePreVectorBounds();
  },
  _primitive_computePreBoundsAfter( transform ) {
    return this._primitive_computePreVectorBoundsAfter(transform) ;
  },

  hasPreview() { return false; },

  otherBrushes( brushes) {
    const colorMap = this._colorMap;
    if( colorMap && colorMap.brush) {
      brushes.push( colorMap.brush );  
    }   
  },

  otherColors( colors = [] ) {
    const colorMap = this._colorMap;
    if( colorMap ) {
      colorMap.getColors(colors);
    }
    return colors;
  },
  buildXml( parentNode ) {
    Xml.addObject(parentNode,this,'Raster');
  },

  generateOutputFigure$() {
    const _this = this, imageLink = _this._imageLink;
    
    if (this._encodedImage != null)
    {
        return resolve(_this);
    }
    else if( imageLink ) {
      if( _this._width && _this._height && ! Config.rasterPreviewForced ) {
        // We do not need to call read info, we have all the info needed cached in the CDL

        if( ! QuantumImage.getInfo(imageLink) ) {
              QuantumImage.setInfo(imageLink, { width : _this.width_px()  ,
                                                height: _this.height_px() });
        }

        return resolve(_this);
      }
      else {

        return QuantumImage.info$(imageLink) .then( info => {

        // Once we start accepting different units, we need to change this
        // This is left for a future extension
        _this._width  = Magnitude.parse( info.width  + 'px' );
        _this._height = Magnitude.parse( info.height + 'px' );

        return _this;
        });
      }
    }
    else {
      // [TODO]
      console.log('imageLink is empty');
      return resolve(null);
    }
  },

  toPrimitive() {
    return this.clone(); // We may create a GRaster
  },

  toGShape() {
    const frame = this.frame_();
    const p0 = frame.p0.clone(), p1 = frame.p1.clone(), p2 = frame.p2.clone(), p3 = frame.p3.clone();
    const curve = Curve([ Segment( p0, p1 ),
                        Segment( p1, p2 ),
                        Segment( p2, p3 ),
                        Segment( p3, p0 ) ], true );
    const regions = [ Region([ curve ]) ];
    return GShape({ polyregions: [ Polyregion({ regions: regions }) ] });
  }
});


Raster.parseXml = function(node, bcFixes) {
  const raster = new Raster();

  Figure._baseParseXml.call(raster, node, Raster,bcFixes);

  // [BC] Remove Pen in case of old CDLs
  const penNode = Xml.child(node,'Pen');
  if( penNode && penNode.getAttribute('Color') ) {
    // Old pen type, CDL 1.0
    raster.pen = EmptyPen();
  }

  return raster;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/raster.js
 **/